import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  docSnapshots,
  collectionChanges,
  getDoc,
  addDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  DocumentData,
  Timestamp,
  query,
  where,
  arrayUnion,
  arrayRemove,
  collectionSnapshots,
  CollectionReference,
  DocumentReference,
  getDocs,
  documentId,
} from '@angular/fire/firestore';
import { Observable, of, from, EMPTY } from 'rxjs';
import { switchMap, mergeMap, map, catchError } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  Storage,
  ref,
  uploadBytesResumable,
  deleteObject,
  getDownloadURL,
  StorageReference,
  UploadTask,
  UploadMetadata,
} from '@angular/fire/storage';
import { UserState } from '../components/user/store/user.reducer';
import * as fromUserSelector from '../components/user/store/user.selectors';
import { v4 as uuidV4 } from 'uuid';
import { format, fromUnixTime } from 'date-fns';
import { OperationNoteId } from '../components/operation-note/store/operation-note.model';
import { OperationId } from '../components/operation/store/operation.model';
import {
  OperationNoteSubtitle,
  OperationNoteSubtitleId,
} from '../components/operation-note-subtitle/store/operation-note-subtitle.model';
import { Apollo, gql } from 'apollo-angular';
import { OperationNoteParticipantId } from '../components/operation-note-participant/store/operation-note-participant.model';

@Injectable({
  providedIn: 'any',
})
export class OperationNoteParticipantService {
  DELETE_OPERATION_NOTE_PARTICIPANTS = gql`
    mutation DeleteAllOperationNoteSubtitles(
      $operationId: String!
      $noteId: String!
    ) {
      deleteAllOperationNoteSubtitles(
        operationId: $operationId
        noteId: $noteId
      )
    }
  `;
  constructor(
    private db: Firestore,
    private fns: Functions,
    private userStore: Store<UserState>,
    private apollo: Apollo
  ) {}

  getAll(note: OperationNoteId): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !note) of([]);
          const dbCollection = collection(
            this.db,
            `organisations/${user.organisation.id}/operations/${note.operation.id}/notes/${note.id}/participants`
          );

          return collectionSnapshots(dbCollection).pipe(
            map((changes) => {
              return changes.map((snap) => {
                const data = snap.data();
                const id = snap.id;
                return { id, ...data };
              });
            })
          );
        })
      );
    } catch (error: any) {
      return of(error?.message);
    }
  }
  addOne(
    note: OperationNoteId,
    participant: OperationNoteParticipantId
  ): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !note || !participant) {
            return EMPTY;
          } else {
            const dbCollection = collection(
              this.db,
              `organisations/${user.organisation.id}/operations/${note.operation.id}/notes/${note.id}/participants/`
            );

            const document: OperationNoteParticipantId = {
              ...participant,
            };

            return from(
              addDoc(dbCollection, document)
                .then((docRef) => {
                  if (!docRef.id) {
                    const succss: string = `Le participant n\'a pas été ajouté ! Veuillez rééssayer`;
                    return of(succss);
                  }

                  const succss: string = `Participant ajouté`;
                  return of(succss);
                })
                .catch((error: any) => of(error?.message))
            ).pipe(mergeMap((res) => res));
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  updateOne(
    note: OperationNoteId,
    participant: Partial<OperationNoteParticipantId | any>
  ): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !note || !participant) {
            return EMPTY;
          } else {
            const dbDocument = doc(
              this.db,
              `organisations/${user.organisation.id}/operations/${note.operation.id}/notes/${note.id}/participants/${participant.id}`
            );

            return from(
              setDoc(
                dbDocument,
                {
                  ...participant,
                  userUpdate: user,
                },
                { merge: true }
              )
                .then(() => of(`Participant modifié`))
                .catch((error: any) => of(error?.message))
            ).pipe(mergeMap((res) => res));
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  deleteOne(note: OperationNoteId, id: string): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !id || !note) {
            return EMPTY;
          } else {
            const dbDocument = doc(
              this.db,
              `organisations/${user.organisation.id}/operations/${note.operation.id}/notes/${note.id}/participants/${id}`
            );

            return from(
              deleteDoc(dbDocument)
                .then(() => of(`Participant supprimé`))
                .catch((error: any) => of(error?.message))
            ).pipe(mergeMap((res) => res));
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }

  deleteAll(note: OperationNoteId): Observable<any> {
    try {
      return this.userStore.select(fromUserSelector.user).pipe(
        switchMap((user) => {
          if (!user || !note) {
            return EMPTY;
          } else {
            const operationId: string = note.operation.id;
            const noteId: string = note.id;

            return this.apollo.mutate({
              mutation: this.DELETE_OPERATION_NOTE_PARTICIPANTS,
              variables: {
                operationId: operationId,
                noteId: noteId,
              },
            });
          }
        })
      );
    } catch (error) {
      return of(error);
    }
  }
}
